import { useState } from "react";

import type { CampaignConflictItem } from "@/@types/campaign-conflict";
import type { IGradeTarget, IReplaceGradeTargetsResponse } from "@/@types/campaign-grade";
import { toast } from "@/components/ui/use-toast";
import {
  useGetItemGradeTargetsQuery,
  useReplaceItemGradeTargetsMutation,
} from "@/services/campaign-pro014";

import { toGradeTargetPayloads } from "../../utils/grade-targets";
import { TOAST_VARIANT } from "../../utils/messages";
import { conflictsFromError, describeApiError } from "../../utils/pro014-api-errors";
import { CampaignConflictModal } from "../campaign-conflict-modal";
import {
  GRADE_CLEARED,
  GRADE_REQUIRED_TO_SAVE,
  GRADE_SAVE_FAILED,
  GRADE_SAVED,
  TARGETS_LOAD_FAILED,
} from "./grade-messages";
import { GradeTargetModal } from "./grade-target-modal";

interface GradeTargetModalConnectedProps {
  campaignCode: number;
  itemCode: string;
  itemName?: string;
  gradeRestricted?: boolean;
  onGradeSaved?: (itemCode: string, hasGrade: boolean) => void;
  onClose: () => void;
}

export function GradeTargetModalConnected({
  campaignCode,
  itemCode,
  itemName,
  gradeRestricted = false,
  onGradeSaved,
  onClose,
}: GradeTargetModalConnectedProps): JSX.Element | null {
  const { data, isLoading, isError } = useGetItemGradeTargetsQuery({
    code: campaignCode,
    itemCode,
  });
  const [replaceTargets, { isLoading: isSaving }] = useReplaceItemGradeTargetsMutation();
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);

  const handleConfirm = async (targets: IGradeTarget[]): Promise<void> => {
    if (gradeRestricted && targets.length === 0) {
      toast({ variant: TOAST_VARIANT.DESTRUCTIVE, description: GRADE_REQUIRED_TO_SAVE });
      return;
    }

    try {
      const result: IReplaceGradeTargetsResponse = await replaceTargets({
        code: campaignCode,
        itemCode,
        targets: toGradeTargetPayloads(itemCode, targets),
      }).unwrap();
      toast({
        variant: TOAST_VARIANT.DEFAULT,
        description: result.hasGrade ? GRADE_SAVED(itemCode) : GRADE_CLEARED(itemCode),
      });
      onGradeSaved?.(itemCode, result.hasGrade);
      onClose();
    } catch (error: unknown) {
      const found: CampaignConflictItem[] = conflictsFromError(error);
      if (found.length > 0) {
        setConflicts(found);
        return;
      }
      toast({ variant: TOAST_VARIANT.DESTRUCTIVE, description: describeApiError(error, GRADE_SAVE_FAILED) });
    }
  };

  // Abrir com lista vazia depois de falhar a leitura faria o PUT apagar as grades reais.
  if (isError) {
    toast({ variant: TOAST_VARIANT.DESTRUCTIVE, description: TARGETS_LOAD_FAILED });
    onClose();
    return null;
  }

  if (isLoading) return null;

  return (
    <>
      <GradeTargetModal
        isOpen
        itemCode={itemCode}
        itemName={itemName}
        initialTargets={data?.targets ?? []}
        isSaving={isSaving}
        onConfirm={handleConfirm}
        onClose={onClose}
      />
      <CampaignConflictModal
        isOpen={conflicts.length > 0}
        conflicts={conflicts}
        onClose={() => setConflicts([])}
        onGoBack={() => setConflicts([])}
      />
    </>
  );
}
